import { OverlapAlertIssue } from 'activity-editor/notifications';
import { AthleteCardProps } from 'activity-editor/panels/athlete-card';
import { AePeriod } from '../context';
import { cloneDeep } from 'lodash';
import { ActivityUpdateBatchResponse, PeriodAthlete } from 'oas';
import { AxiosResponse } from 'axios';

export const checkPeriodOverlap = (card: AthleteCardProps, updatedPeriod: AePeriod, allPeriods: AePeriod[]) => {
    const issues: OverlapAlertIssue[] = [];
    if (allPeriods.length > 1) {
        allPeriods.forEach((otherPeriod) => {
            if (
                otherPeriod.id !== updatedPeriod.id &&
                updatedPeriod.startDateTime <= otherPeriod.endDateTime &&
                updatedPeriod.endDateTime >= otherPeriod.startDateTime &&
                otherPeriod.athleteIds.includes(card.id)
            ) {
                issues.push({
                    athleteId: card.id,
                    athleteDetail: card.fullName,
                    cannotAddTo: updatedPeriod.name,
                    presentOn: otherPeriod.name,
                });
            }
        });
    }

    if (issues.length) {
        return issues;
    }

    return false;
};

export const addAthletesToPeriods = (
    cards: AthleteCardProps[],
    periods: AePeriod[],
    selectedPeriodIds: string[],
): [OverlapAlertIssue[], OverlapAlertIssue[], AePeriod[], boolean] => {
    let isDirty = false;
    const issues: OverlapAlertIssue[] = [];
    const overlappingIssues: OverlapAlertIssue[] = [];
    const newPeriods = cloneDeep(periods);

    // Loop the athlete cards and create new periods and identify issues
    cards.forEach((card) => {
        newPeriods.forEach((period) => {
            // only add athlete to selected period(s) and if it is not available on selected period(s)
            if (selectedPeriodIds.includes(period.id)) {
                // If athlete is already added to selected periods add to issue
                if (period.athleteIds.includes(card.id)) {
                    issues.push({
                        athleteId: card.id,
                        athleteDetail: card.fullName,
                        cannotAddTo: period.name,
                        presentOn: period.name,
                    });

                    return;
                } else {
                    const newRotation = {
                        start_time_ms: period.startDateTime.getTime(),
                        end_time_ms: period.endDateTime.getTime(),
                    };
                    const newPeriodAthlete = {
                        id: '',
                        athlete_id: card.id,
                        period_id: period.id,
                        rotations: [newRotation],
                        is_injected: 0,
                    } as PeriodAthlete;

                    period.periodAthletes.push(newPeriodAthlete);
                }

                // Check overlap
                const overlapIssues = checkPeriodOverlap(card, period, newPeriods);
                if (overlapIssues) {
                    overlappingIssues.push(...overlapIssues);
                } else {
                    period.athleteIds.push(card.id);
                    isDirty = true;
                }
            }
            period.athleteCount = period.athleteIds.length;
        });
    });

    // Return the issues, new periods and if there is change to the period
    return [issues, overlappingIssues, newPeriods, isDirty];
};

export const validateAddAthletesToPeriods = (
    cards: AthleteCardProps[],
    periods: AePeriod[],
    selectedPeriodIds: string[],
) => {
    const issues: OverlapAlertIssue[] = [];
    const overlappingIssues: OverlapAlertIssue[] = [];
    const newPeriods = cloneDeep(periods);

    // Loops the athlete cards and identify issues
    cards.forEach((card) => {
        newPeriods.forEach((period) => {
            if (selectedPeriodIds.includes(period.id)) {
                if (period.athleteIds.includes(card.id)) {
                    // If athlete is already added to selected periods
                    issues.push({
                        athleteId: card.id,
                        athleteDetail: card.fullName,
                        cannotAddTo: period.name,
                        presentOn: period.name,
                    });
                    return;
                }
                const overlapIssues = checkPeriodOverlap(card, period, newPeriods);
                if (overlapIssues) {
                    issues.push(...overlapIssues);
                    overlappingIssues.push(...overlapIssues);
                }
            }
        });
    });

    // Return identify issues
    return { issues: issues, overlappingIssues: overlappingIssues };
};

export interface OverlappingDetails {
    periodName: string;
    periodId: string;
    activityName: string;
    activityId: string;
    athleteName: string;
}

export const checkActivityOverlapResponse = (
    result: AxiosResponse<ActivityUpdateBatchResponse>,
    athleteCards: AthleteCardProps[],
): OverlappingDetails[] => {
    if (!result.data || result.data?.error === undefined) {
        return [];
    }

    return (
        result.data.error.periods?.flatMap(
            (periodError) =>
                periodError.athletes?.flatMap(
                    (athlete) =>
                        athlete.errors
                            ?.filter(
                                (err) =>
                                    err.error_code === 'concurrent_periods' && Array.isArray(err.overlapping_periods),
                            )
                            .flatMap(
                                (err) =>
                                    err.overlapping_periods?.map((overlap) => {
                                        const matchedAthleteCard = athleteCards.find(
                                            (card) => card.id === athlete.athlete_id,
                                        );

                                        return {
                                            periodId: overlap.id ?? '',
                                            periodName: overlap.name ?? '',
                                            athleteName: matchedAthleteCard?.fullName ?? '',
                                            activityName: overlap.activity_name ?? '',
                                            activityId: overlap.activity_id ?? '',
                                        };
                                    }) ?? [],
                            ) ?? [],
                ) ?? [],
        ) ?? []
    );
};
